import { Box, MenuItem, Select, TextField, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTime } from 'luxon';

import SectionHeader from 'components/Common/SectionHeader';
import ConnectForm from 'components/RouteWizard/ConnectForm';
import { Controller, useFormContext } from 'react-hook-form';
import DateQuickButton from './DateQuickButton';
import { hourArray, minuteArray } from './timeOptions';

const LegacyDateTimeSelect = ({
  namePrefix,
  title,
  leftQuickButtonLabel,
  leftOnClick,
  rightQuickButtonLabel,
  rightOnClick,
  disabled = false,
  inlineStyle = false,
}: any) => {
  const { control } = useFormContext()

  const date = new Date()
  date.setDate(date.getDate() - 1)

  return (
    <Controller
      control={control}
      name={`${namePrefix}.when`}
      defaultValue={new Date()}
      render={({
        field: { ...fieldProps },
        fieldState: { ...filedStateProps },
      }) => {
        return (
          <Box
            component="div"
            sx={{
              display: 'flex',
              flexDirection: inlineStyle ? 'row' : 'column',
              alignItems: 'stretch',
              flexGrow: '1',
            }}
          >
            <ConnectForm>
              <SectionHeader
                title={title}
                containerStyles={{
                  alignSelf: 'center',
                  marginBottom: '10px',
                  textTransform: 'uppercase',
                }}
              />
              <Box
                component="div"
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  marginBottom: inlineStyle ? '0px' : '5px',
                }}
              >
                <DateQuickButton
                  label={leftQuickButtonLabel}
                  onClick={leftOnClick}
                  disabled={disabled}
                />
                <DateQuickButton
                  label={rightQuickButtonLabel}
                  onClick={rightOnClick}
                  disabled={disabled}
                />
              </Box>
              <LocalizationProvider
                dateAdapter={AdapterLuxon}
                adapterLocale="en-GB"
              >
                <DatePicker
                  // Luxon DateTime for minDate/value
                  minDate={DateTime.now().minus({ days: 1 }).startOf('day')}
                  value={DateTime.fromJSDate(fieldProps.value)}
                  onChange={(dt) => {
                    // Guard invalid/typing states
                    if (!dt || !dt.isValid) return

                    // Preserve time from current JS Date, apply picked Y/M/D
                    const curr = fieldProps.value ?? new Date()
                    const next = DateTime.fromJSDate(curr).set({
                      year: dt.year,
                      month: dt.month,
                      day: dt.day,
                    })

                    fieldProps.onChange(next.toJSDate())
                  }}
                  renderInput={(params) => <TextField {...params} />}
                  disabled={disabled}
                />
              </LocalizationProvider>
              <Box
                component="div"
                sx={{
                  marginTop: inlineStyle ? '0px' : '20px',
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  gap: '10px',
                }}
              >
                <Select
                  labelId={`hour-select-label`}
                  id={`hour-select`}
                  value={fieldProps.value?.getHours() ?? 0}
                  defaultValue={0}
                  onChange={(e) => {
                    const hour = Number(e.target.value)
                    const v = fieldProps.value ?? new Date()
                    const newDate = new Date(
                      v.getFullYear(),
                      v.getMonth(),
                      v.getDate(),
                      hour,
                      v.getMinutes(),
                      0,
                    )
                    fieldProps.onChange(newDate)
                  }}
                  sx={{ flexGrow: 1 }}
                  disabled={disabled}
                >
                  {hourArray.map(({ value, label }) => {
                    return (
                      <MenuItem key={value} value={value}>
                        {label}
                      </MenuItem>
                    )
                  })}
                </Select>

                <Select
                  labelId={`minute-select-label`}
                  id={`minute-select`}
                  // use floor to avoid 60 on :59
                  value={
                    fieldProps.value
                      ? Math.floor(fieldProps.value.getMinutes() / 5) * 5
                      : 0
                  }
                  onChange={(e) => {
                    const minute = Number(e.target.value)
                    const v = fieldProps.value ?? new Date()
                    const newDate = new Date(
                      v.getFullYear(),
                      v.getMonth(),
                      v.getDate(),
                      v.getHours(),
                      minute,
                      0,
                    )
                    fieldProps.onChange(newDate)
                  }}
                  sx={{ flexGrow: 1 }}
                  disabled={disabled}
                >
                  {minuteArray.map(({ value, label }) => {
                    return (
                      <MenuItem key={value} value={value}>
                        {label}
                      </MenuItem>
                    )
                  })}
                </Select>
              </Box>
            </ConnectForm>
            {filedStateProps?.error?.message && (
              <Typography color="error" sx={{ maxWidth: '100%' }}>
                {filedStateProps?.error?.message || 'Selected time not valid'}
              </Typography>
            )}
          </Box>
        )
      }}
    />
  )
}

export default LegacyDateTimeSelect
